/**
 * @format
 * Google Extended Access mixins
 * 
 * Example: https://play.google.com/newsstand/api/v3/articleaccess?testurl=https://zelacom:showme!@test.newsatomic.com/news/2025/10/06/proposed-2026-ulster-county-budget/475hPE#swg.debug=1
 * Simulated: https://play.google.com/newsstand/api/v3/articleaccess?testurl=https://zelacom:showme!@test.newsatomic.com/columns/2025/12/04/from-the-forest/062vJW#swg.debug=1
 *
 * Test Panel 1:
 * https://play.google.com/newsstand/api/v3/articleaccess?testurl=https://zelacom:showme!@test.newsatomic.com/columns/2025/12/04/pet-sounds/435zXL#swg.debug=1
 * 
 * Test Panel 2:
 * https://play.google.com/newsstand/api/v3/articleaccess?testurl=https://zelacom:showme!@test.newsatomic.com/columns/2025/12/04/from-the-forest/062vJW#swg.debug=1
 * 
 * Test Panel 3:
 * https://play.google.com/newsstand/api/v3/articleaccess?testurl=https://zelacom:showme!@test.newsatomic.com/news/2025/12/09/doug-rowles-lights-up-the-night/867yjp#swg.debug=1
 * 
 * Test Panel 4:
 * https://play.google.com/newsstand/api/v3/articleaccess?testurl=https://zelacom:showme!@test.newsatomic.com/columns/2025/11/20/more-stories-on-the-wind/946LcJ#swg.debug=1
 * 
 * Test Panel 5:
 * https://play.google.com/newsstand/api/v3/articleaccess?testurl=https://zelacom:showme!@test.newsatomic.com/columns/2025/11/20/gardening-for-a-better-world/928okZ#swg.debug=1
 * 
 */

import { loadUserState } from '@/services/google-extended-access.js';

// Global flag to prevent multiple GAA initializations
window._gaaInitialized = window._gaaInitialized || false;

export const googleExtendAccessMixin = {
    data() {
        return {
            googleExtendedAccessScriptsLoaded: false,
            googleApiClientId: null,
            gaaCtaShown: false, // Track if Extended Access CTA was shown
            gaaCtaShownTimestamp: null, // Timestamp when CTA was shown
            gaaInitTimestamp: null, // Timestamp when GAA was initialized (for timing checks)
        };
    },

    methods: {
        /**
     * Get the Google API client ID from the meta tag
         * 
         * @returns {String|null} The Google API client ID or null if not found
         */
        getGoogleApiClientId() {
            if (this.googleApiClientId) {
                return this.googleApiClientId;
            }
            
            const metaTag = document.querySelector('meta[name="google-signin-client_id"]');
            if (metaTag && metaTag.content) {
                this.googleApiClientId = metaTag.content;
                return this.googleApiClientId;
            }
            
            console.warn('Google API Client ID not found in meta tag');
            return null;
        },

        /**
         * Load the user state for Google Extended Access for a given post.
         * This state is used to determine if the user has access to the post (e.g. authenticated user who can pay for the post, or authenticated user who is a subscriber, or anonymous user who must sign-up, etc.)
         * 
         * @param {Object} data Data.  Must include postId.
         * 
         * @return Promise for resolve, reject
         */
        loadUserState(data) {
            return loadUserState(data);
        },
        /**
         * Load the Google scripts for Google Extended Access.
         * 
         * @param {Number} postId The post ID to initialize metering for
         * @return Promise for resolve, reject
         */
        async loadGoogleExtendedAccessScripts(postId) {
            if (this.googleExtendedAccessScriptsLoaded) {
                // Scripts already loaded, just initialize metering
                this.initGoogleExtendedAccessMetering(postId);
                return;
            }
            
            try {
                // Load Google Identity Services
                await this.loadGoogleExtendedAccessScript('https://accounts.google.com/gsi/client');
                
                // Load Google News SWG
                await this.loadGoogleExtendedAccessScript('https://news.google.com/swg/js/v1/swg.js', {
                    'subscriptions-control': 'manual',
                });

                // Load GAA metering script
                await this.loadGoogleExtendedAccessScript('https://news.google.com/swg/js/v1/swg-gaa.js', null, () => {
                    this.googleExtendedAccessScriptsLoaded = true;
                    return this.initGoogleExtendedAccessMetering(postId);
                });
            } catch (error) {
                console.error('Failed to load Google Extended Access scripts:', error);
                this.error = 'Failed to load Google Extended Access scripts';
            }
        },

        /**
         * Load a script.
         * 
         * @param {String} src The source of the script.
         * @param {Object} attributes The attributes of the script.
         * @param {Function} onload The function to call when the script is loaded.
         * 
         * @return Promise for resolve, reject
         */
        loadGoogleExtendedAccessScript(src, attributes = null, onload = null) {
            return new Promise((resolve, reject) => {
                // Check if script already exists
                const existingScript = document.querySelector(`script[src="${src}"]`);
                if (existingScript) {
                    if (onload) onload();
                    resolve();
                    return;
                }
                
                const script = document.createElement('script');
                script.src = src;
                script.async = true;
                script.defer = true;
                
                // Add custom attributes
                if (attributes) {
                    Object.keys(attributes).forEach(key => {
                        script.setAttribute(key, attributes[key]);
                    });
                }
                
                // Handle onload
                if (onload) {
                    script.onload = () => {
                        onload();
                        resolve();
                    };
                } else {
                    script.onload = resolve;
                }
                
                script.onerror = reject;
                
                document.head.appendChild(script);
            });
        },

        /**
         * Initialize Google Extended Access metering.
         * 
         * @param {Number} postId The post ID to get user state for
         *
         * @returns 
         */
        initGoogleExtendedAccessMetering(postId) {
            if (!postId) {
                console.warn('Google Extended Access: postId is required');
                return;
            }
            
            // Check if already initialized to prevent "already configured" error
            if (window._gaaInitialized) {
                return;
            }
            
            // Check if we're running on localhost - Google Extended Access doesn't work with localhost
            if (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') {
                console.warn('Google Extended Access: Cannot initialize on localhost. Google Extended Access requires a proper domain name registered in Google Cloud Console.');
                console.warn('Please access your site through a proper domain name (e.g., yoursite.com) instead of localhost.');
                console.warn('To test Google Extended Access, you need to:');
                console.warn('1. Set up a proper domain name in your hosts file or use a service like ngrok');
                console.warn('2. Add the domain to your Google Cloud Console OAuth configuration');
                console.warn('3. Access your site through that domain name');
                
                // Check if we're in development mode and should allow localhost testing
                const isDevelopmentMode = window.location.search.includes('gaa_debug=1') || 
                                       window.location.search.includes('swg.debug=1');
                
                if (!isDevelopmentMode) {
                    return;
                } else {
                    console.warn('Development mode detected - proceeding with localhost (this will likely fail)');
                }
            }
            
            try {
                // Create the publisherEntitlementPromise that uses the already-loaded user state
                const publisherEntitlementPromise = new Promise((resolve) => {
                    // Use the user state that was already loaded in the component
                    const userState = this.userState || {
                        granted: false,
                        id: null
                    };
                    resolve(userState);
                });

                // Create the registerUserPromise for new user registration
                // Google handles the OAuth flow and redirects back with gaaUser data
                const registerUserPromise = new Promise((resolve) => {
                    window.GaaMetering.getGaaUserPromise().then((gaaUser) => {
                        // Send the gaaUser data to our backend to register the user
                        this.registerGoogleUser(gaaUser, postId)
                            .then(registeredUserState => {
                                // Update the parent window's user authentication state
                                if (registeredUserState.granted && registeredUserState.id) {
                                    window.userAuthenticated = true;
                                    // Trigger event to update UI
                                    window.dispatchEvent(new CustomEvent('gaa-login-complete', {
                                        detail: { userState: registeredUserState }
                                    }));
                                }
                                
                                resolve(registeredUserState);
                            })
                            .catch(error => {
                                console.error('Failed to register user:', error);
                                // Return default user state for failed registration
                                resolve({
                                    granted: false,
                                    id: null
                                });
                            });
                    });
                });

                // Create the handleLoginPromise for existing user login
                const handleLoginPromise = new Promise((resolve) => {
                    window.GaaMetering.getLoginPromise().then(() => {
                        // Open login window or redirect to login
                        this.handleUserLogin(postId)
                            .then(loggedInUserState => {
                                resolve(loggedInUserState);
                            })
                            .catch(error => {
                                console.error('Failed to handle user login:', error);
                                // Always resolve (never reject) so Google Extended Access can reset properly
                                // Return default user state for failed/cancelled login
                                resolve({
                                    granted: false,
                                    id: null
                                });
                            });
                    }).catch(error => {
                        console.error('Failed to get login promise:', error);
                        // If getLoginPromise itself fails, still resolve with default state
                        resolve({
                            granted: false,
                            id: null
                        });
                    });
                });

                // Initialize with all required callbacks
                if (typeof window.GaaMetering !== 'undefined' && window.GaaMetering.init) {
                    const clientId = this.getGoogleApiClientId();
                    if (!clientId) {
                        console.error('Google API Client ID is required for Google Extended Access');
                        return;
                    }
                    
                    const allowedReferrers = this.getAllowedReferrers();
                    
                    // Fallback if getAllowedReferrers returns empty array
                    const finalAllowedReferrers = allowedReferrers && allowedReferrers.length > 0 ? allowedReferrers : [
                        window.location.origin,
                        window.location.hostname
                    ];
                    
                    // Get the actual user state from the component and serialize it properly
                    const rawUserState = this.userState || {
                        granted: false,
                        id: null
                    };
                    
                    // Serialize the user state to remove Vue reactive properties
                    const currentUserState = JSON.parse(JSON.stringify(rawUserState));
                    
                    // Hash the user ID to avoid PII (Google recommends hash string instead of number)
                    let hashedId = currentUserState.id || null;
                    if (hashedId && typeof hashedId === 'number') {
                        // Convert number to string and create a simple hash
                        // Using a combination of user ID and a salt to create a consistent hash
                        const idString = String(hashedId);
                        // Simple hash function - creates a consistent hash from the user ID
                        // This avoids PII while maintaining consistency for the same user
                        let hash = 0;
                        for (let i = 0; i < idString.length; i++) {
                            const char = idString.charCodeAt(i);
                            hash = ((hash << 5) - hash) + char;
                            hash = hash & hash; // Convert to 32-bit integer
                        }
                        // Convert to positive hex string
                        hashedId = Math.abs(hash).toString(16);
                    }
                    
                    // Ensure user state has required structure for GAA
                    // IMPORTANT: For logged-in users who have reached the Extended Access limit,
                    // we intentionally omit registrationTimestamp to prevent Google from granting Extended Access.
                    // For anonymous users, registrationTimestamp should be null (which is fine - they'll see registration intervention).
                    // For logged-in users with Extended Access available, registrationTimestamp should be present (a number).
                    const hasId = !!hashedId;
                    const registrationTimestampValue = currentUserState.registrationTimestamp;
                    
                    // Determine if we should omit registrationTimestamp:
                    // - Omit if: user is logged in (has id) AND registrationTimestamp is null/undefined (limit reached)
                    // - Include if: user is anonymous (no id) OR registrationTimestamp is a number (Extended Access available)
                    const shouldOmitRegistrationTimestamp = hasId && (registrationTimestampValue === null || registrationTimestampValue === undefined);
                    
                    const validatedUserState = {
                        granted: currentUserState.granted || false,
                        id: hashedId,
                        // Only omit registrationTimestamp for logged-in users who have reached the limit
                        // For anonymous users, include null so Google knows to show registration intervention
                        // For logged-in users with Extended Access, include the timestamp number
                        ...(shouldOmitRegistrationTimestamp 
                            ? {} // Omit registrationTimestamp for logged-in users at limit
                            : { registrationTimestamp: registrationTimestampValue !== undefined ? registrationTimestampValue : null }), // Include null for anonymous users, number for logged-in users
                        subscriptionTimestamp: currentUserState.subscriptionTimestamp || null,
                        grantReason: currentUserState.grantReason || null
                    };
                    
                    const gaaConfig = {
                        googleApiClientId: clientId,
                        
                        // Use the validated user state for GAA
                        userState: validatedUserState,
                        
                        // Required promises and callbacks
                        publisherEntitlementPromise: publisherEntitlementPromise,
                        registerUserPromise: registerUserPromise,
                        handleLoginPromise: handleLoginPromise,
                        
                        // Callback functions
                        unlockArticle: this.unlockArticle.bind(this),
                        showPaywall: this.showPaywall.bind(this),
                        // Note: onSubscribeRequest should be set AFTER init, not in config
                        //handleSwGEntitlement: this.handleSwGEntitlement,
                        
                        // Configuration
                        allowedReferrers: finalAllowedReferrers,
                        paywallType: 'CLIENT_SIDE'
                    };
                    
                    // Store reference to 'this' for use in callbacks BEFORE init
                    const self = this;
                    
                    // Set up onSubscribeRequest callback BEFORE initialization
                    // Some implementations require it to be set before init
                    if (window.GaaMetering && !window._gaaInitialized) {
                        const subscribeHandler = function(subscribeRequest) {
                            try {
                                self.handleSubscribeRequest(subscribeRequest);
                            } catch (error) {
                                console.error('DEBUG: Error in onSubscribeRequest handler:', error);
                                console.error('DEBUG: Error stack:', error.stack);
                            }
                        };
                        
                        window.GaaMetering.onSubscribeRequest = subscribeHandler;
                    }
                    
                    // Try to initialize GAA with error handling
                    try {
                        // Track when GAA was initialized (for timing checks in showPaywall)
                        this.gaaInitTimestamp = Date.now();
                        
                        window.GaaMetering.init(gaaConfig);
                        window._gaaInitialized = true;
                        
                        // Set up onSubscribeRequest callback AFTER initialization (in case it needs to be set after)
                        // According to Google documentation, this should be set as a property on GaaMetering (not in config)
                        
                        if (window.GaaMetering) {
                            // Set onSubscribeRequest as a property (not in config)
                            // This is the correct way according to Google's documentation:
                            // window.GaaMetering.onSubscribeRequest = function() { ... }
                            const subscribeHandler = function(subscribeRequest) {
                                try {
                                    self.handleSubscribeRequest(subscribeRequest);
                                } catch (error) {
                                    console.error('DEBUG: Error in onSubscribeRequest handler:', error);
                                    console.error('DEBUG: Error stack:', error.stack);
                                }
                            };
                            
                            // Set the callback
                            window.GaaMetering.onSubscribeRequest = subscribeHandler;
                            
                            // Test if we can access it (for debugging)
                            try {
                                if (typeof window.GaaMetering.onSubscribeRequest === 'function') {
                                    // Store reference globally for manual testing
                                    window._testGaaSubscribe = function() {
                                        window.GaaMetering.onSubscribeRequest({ test: true });
                                    };
                                }
                            } catch (e) {
                                console.error('DEBUG: Could not verify onSubscribeRequest:', e);
                            }

                            // Also try setting it as a getter/setter in case Google expects that
                            try {
                                Object.defineProperty(window.GaaMetering, 'onSubscribeRequest', {
                                    value: subscribeHandler,
                                    writable: true,
                                    enumerable: true,
                                    configurable: true
                                });
                            } catch (e) {
                                console.error('DEBUG: Could not set via defineProperty:', e);
                            }
                            
                            // Listen for postMessage events from Google's iframe
                            // Google might send subscription requests via postMessage instead of calling the callback directly
                            const messageHandler = (event) => {
                                // Only accept messages from Google domains
                                if (event.origin.includes('google.com') || event.origin.includes('news.google.com')) {
                                    
                                    // Check if this is a subscription request
                                    if (event.data && (
                                        event.data.type === 'subscribe' ||
                                        event.data.action === 'subscribe' ||
                                        event.data.command === 'subscribe' ||
                                        (typeof event.data === 'string' && event.data.includes('subscribe'))
                                    )) {
                                        subscribeHandler(event.data);
                                    }
                                }
                            };
                            
                            window.addEventListener('message', messageHandler);
                            
                            // Store handler reference for cleanup if needed
                            window._gaaMessageHandler = messageHandler;
                        } else {
                            console.error('DEBUG: GaaMetering not available after init to set onSubscribeRequest');
                        }
                        
                        // Also try to intercept clicks on Google's CTA subscribe button directly as a fallback
                        // Google renders the CTA dynamically, so we need to watch for it
                        setTimeout(() => {
                            // Use MutationObserver to watch for Google's CTA being added to the DOM
                            const observer = new MutationObserver((mutations) => {
                                mutations.forEach((mutation) => {
                                    mutation.addedNodes.forEach((node) => {
                                        if (node.nodeType === 1) { // Element node
                                            // Look for Google's CTA container - it might be in a specific container
                                            // Google often uses classes like 'swg-dialog', 'swg-gaa', etc.
                                            const googleCtaContainers = node.querySelectorAll ? node.querySelectorAll('.swg-dialog, .swg-gaa, [class*="swg"], [id*="swg"], [data-gaa], iframe[src*="google"], iframe[src*="news.google"]') : [];
                                            
                                            // Also check if the node itself is a Google CTA element
                                            if (node.matches && (node.matches('.swg-dialog, .swg-gaa, [class*="swg"], [id*="swg"], [data-gaa]') || 
                                                (node.tagName === 'IFRAME' && (node.src.includes('google') || node.src.includes('news.google'))))) {
                                                checkForGoogleSubscribeButton();
                                            }
                                            
                                            googleCtaContainers.forEach(container => {
                                                checkForGoogleSubscribeButton();
                                            });
                                        }
                                    });
                                });
                            });
                            
                            // Start observing the document body for changes
                            observer.observe(document.body, {
                                childList: true,
                                subtree: true
                            });
                            
                            const checkForGoogleSubscribeButton = () => {
                                // Look specifically for Google's CTA button - it's likely in a dialog or overlay
                                // Google Extended Access CTA buttons might have specific attributes
                                const googleCtaSelectors = [
                                    // Look in Google's containers first
                                    '.swg-dialog button',
                                    '.swg-gaa button',
                                    '[class*="swg"] button:not(.na-subscribe-to button):not(.navbar button):not(nav button)',
                                    '[data-gaa] button',
                                    // Look for buttons with specific Google attributes
                                    'button[data-gaa-subscribe]',
                                    'button[aria-label*="Subscribe" i]:not(.na-subscribe-to):not(.navbar-item)',
                                    // Look for buttons in overlays/modals that Google might create
                                    '[role="dialog"] button',
                                    '[role="alertdialog"] button'
                                ];
                                
                                let foundGoogleButton = false;
                                
                                googleCtaSelectors.forEach(selector => {
                                    try {
                                        const buttons = document.querySelectorAll(selector);
                                        buttons.forEach(btn => {
                                            // Skip buttons we've already processed or that are clearly not Google's
                                            // Exclude site's own buttons more aggressively
                                            if (btn.dataset.gaaGoogleHandlerAdded || 
                                                btn.closest('.na-subscribe-to') || 
                                                btn.closest('.navbar') ||
                                                btn.closest('nav') ||
                                                btn.closest('header') ||
                                                btn.classList.contains('na-subscribe-to') ||
                                                btn.classList.contains('navbar-item') ||
                                                btn.closest('[class*="na-"]') && !btn.closest('[class*="swg"]')) {
                                                return;
                                            }
                                            
                                            const text = (btn.textContent || btn.innerText || '').trim();
                                            const ariaLabel = btn.getAttribute('aria-label') || '';
                                            
                                            // Check if this looks like Google's CTA button
                                            // Google's button is usually in a dialog/overlay and has specific styling
                                            const isInGoogleContainer = btn.closest('.swg-dialog, .swg-gaa, [class*="swg"], [data-gaa]');
                                            const hasGoogleText = text.toLowerCase().includes('subscribe') && 
                                                                 (isInGoogleContainer || btn.hasAttribute('data-gaa-subscribe'));
                                            
                                            if (hasGoogleText || (ariaLabel.toLowerCase().includes('subscribe') && isInGoogleContainer)) {
                                                btn.dataset.gaaGoogleHandlerAdded = 'true';
                                                foundGoogleButton = true;
                                                
                                                // Add click handler with capture phase to intercept
                                                btn.addEventListener('click', (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    e.stopImmediatePropagation();
                                                    
                                                    // Call our handler immediately
                                                    self.handleSubscribeRequest({ source: 'google-cta-button-click', event: e });
                                                }, true); // Use capture phase to intercept before Google's handler
                                            }
                                        });
                                    } catch (e) {
                                        // Ignore selector errors
                                    }
                                });
                                
                                // Also check iframes - Google might render the CTA in an iframe
                                const iframes = document.querySelectorAll('iframe[src*="google"], iframe[src*="news.google"]');
                                iframes.forEach(iframe => {
                                    if (!iframe.dataset.gaaIframeWatched) {
                                        iframe.dataset.gaaIframeWatched = 'true';
                                        
                                        // Try to access iframe content (might be cross-origin, so this might fail)
                                        try {
                                            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                                            if (iframeDoc) {
                                                const iframeButtons = iframeDoc.querySelectorAll('button');
                                                iframeButtons.forEach(btn => {
                                                    const text = (btn.textContent || '').trim();
                                                    if (text.toLowerCase().includes('subscribe')) {
                                                        btn.addEventListener('click', (e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            self.handleSubscribeRequest({ source: 'google-iframe-button-click', event: e });
                                                        }, true);
                                                    }
                                                });
                                            }
                                        } catch (e) {
                                            // Cross-origin iframe, can't access content
                                            console.warn('Cannot access iframe content (likely cross-origin):', e);
                                        }
                                    }
                                });
                                
                                return foundGoogleButton;
                            };
                            
                            // Check immediately
                            checkForGoogleSubscribeButton();
                            
                            // Also check periodically as a backup
                            const intervalId = setInterval(() => {
                                if (checkForGoogleSubscribeButton()) {
                                    // Found the button, can stop checking so frequently
                                    clearInterval(intervalId);
                                }
                            }, 500);
                            
                            // Stop checking after 30 seconds
                            setTimeout(() => {
                                clearInterval(intervalId);
                                observer.disconnect();
                            }, 30000);
                        }, 2000); // Wait 2 seconds for Google to render the CTA
                        
                        // Log additional info for debugging CTA display
                        const shouldShowCTA = !gaaConfig.userState.granted && 
                                            !!gaaConfig.userState.id && 
                                            !!gaaConfig.userState.registrationTimestamp && 
                                            !gaaConfig.userState.subscriptionTimestamp;
                        
                        // Track when CTA should be shown (for detecting Subscribe clicks)
                        // We'll mark it as shown when we actually detect it in the DOM, not just when it should be shown
                        // This prevents false positives in showPaywall detection
                        if (shouldShowCTA) {
                            // Try to detect when CTA is actually shown by watching for Google's iframe/dialog
                            const detectCtaShown = () => {
                                // Look for Google's CTA dialog/iframe - check multiple selectors
                                const selectors = [
                                    '.swg-dialog',
                                    'iframe.swg-dialog',
                                    'iframe[class*="swg"]',
                                    '[class*="swg-dialog"]',
                                    'iframe[src*="news.google.com/swg"]'
                                ];
                                
                                for (const selector of selectors) {
                                    const element = document.querySelector(selector);
                                    if (element) {
                                        // Check if it's visible (has dimensions or is in viewport)
                                        const rect = element.getBoundingClientRect();
                                        const isVisible = (rect.width > 0 && rect.height > 0) || 
                                                         element.offsetHeight > 0 || 
                                                         element.offsetWidth > 0 ||
                                                         window.getComputedStyle(element).display !== 'none';
                                        
                                        if (isVisible) {
                                            // CTA is actually visible
                                            this.gaaCtaShown = true;
                                            this.gaaCtaShownTimestamp = Date.now();
                                            return true;
                                        }
                                    }
                                }
                                return false;
                            };
                            
                            // Check immediately and then periodically
                            if (!detectCtaShown()) {
                                const checkInterval = setInterval(() => {
                                    if (detectCtaShown()) {
                                        clearInterval(checkInterval);
                                    }
                                }, 500);
                                
                                // Stop checking after 15 seconds
                                // If we haven't detected it by then, assume Google will show it (or won't)
                                setTimeout(() => {
                                    clearInterval(checkInterval);
                                    if (!this.gaaCtaShown) {
                                        console.warn('CTA not detected in DOM after 15 seconds');
                                        // Don't set gaaCtaShown = true here - let showPaywall handle it
                                    }
                                }, 15000);
                            }
                        }
                    } catch (error) {
                        console.error('DEBUG: GaaMetering.init failed:', error);
                        
                        // If error is "already configured", just set the flag
                        if (error.message && error.message.includes('already configured')) {
                            window._gaaInitialized = true;
                        } else {
                            
                            // Retry after a short delay
                            setTimeout(() => {
                                try {
                                    window.GaaMetering.init(gaaConfig);
                                    window._gaaInitialized = true;
                                } catch (retryError) {
                                    console.error('DEBUG: GaaMetering.init retry failed:', retryError);
                                    if (retryError.message && retryError.message.includes('already configured')) {
                                        window._gaaInitialized = true;
                                    }
                                }
                            }, 1000);
                        }
                    }
                } else {
                    console.warn('GaaMetering not available yet, waiting...');
                    // Wait for GaaMetering to be available
                    this.waitForGaaMetering(postId);
                }
            } catch (error) {
                console.error('Failed to initialize Google Extended Access metering:', error);
            }
        },

        /**
         * Wait for GaaMetering to become available and then initialize
         * 
         * @param {Number} postId The post ID to get user state for
         */
        waitForGaaMetering(postId) {
            const checkInterval = setInterval(() => {
                if (typeof window.GaaMetering !== 'undefined' && window.GaaMetering.init) {
                    clearInterval(checkInterval);
                    this.initGoogleExtendedAccessMetering(postId);
                }
            }, 100);
            
            // Stop checking after 10 seconds
            setTimeout(() => {
                clearInterval(checkInterval);
            }, 10000);
        },

        /**
         * Register a new user via Google Extended Access
         * Google handles the OAuth flow and provides gaaUser data
         * 
         * @param {Object} gaaUser Google user data from GaaMetering.getGaaUserPromise()
         * @param {Number} postId The post ID
         * @returns {Promise} Promise that resolves with userState
         */
        async registerGoogleUser(gaaUser, postId) {
            try {
                // Use window.axios which has CSRF tokens and credentials configured
                const response = await window.axios.post('/api/gaa/register-user', {
                    gaaUser: gaaUser,
                    postId: postId
                });
                
                if (response.data.success && response.data.data) {
                    const userState = response.data.data;
                    
                    // Ensure required fields are present
                    if (!userState.registrationTimestamp) {
                        userState.registrationTimestamp = Math.floor(Date.now() / 1000);
                    }
                    
                    return userState;
                } else {
                    throw new Error(response.data.error || 'Registration failed');
                }
            } catch (error) {
                console.error('Failed to register Google user:', error);
                
                throw error;
            }
        },

        /**
         * Handle user registration for new users using cross-window messaging
         * NOTE: This is currently not used - Google handles registration OAuth directly
         * 
         * @param {Number} postId The post ID
         * @returns {Promise} Promise that resolves with userState
         */
        async handleUserRegister(postId) {
            return new Promise((resolve, reject) => {
                // Build the registration URL with parameters
                const registerUrl = `/gaa/register?post_id=${postId}&origin=${encodeURIComponent(window.location.origin)}`;
                
                // Open registration window as a centered popup
                const width = 500;
                const height = 700; // Slightly taller for registration form
                const left = (window.screen.width - width) / 2;
                const top = (window.screen.height - height) / 2;
                
                const registerWindow = window.open(
                    registerUrl,
                    'gaa-register',
                    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
                );
                
                if (!registerWindow) {
                    reject(new Error('Failed to open registration window. Please disable your popup blocker.'));
                    return;
                }
                
                let checkWindowClosed = null;
                let timeoutId = null;
                
                // Cleanup function
                const cleanup = () => {
                    if (checkWindowClosed) {
                        clearInterval(checkWindowClosed);
                        checkWindowClosed = null;
                    }
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                        timeoutId = null;
                    }
                    window.removeEventListener('message', messageHandler);
                };
                
                // Listen for messages from the registration window
                const messageHandler = (event) => {
                    // Verify the message is from our registration window
                    if (event.source !== registerWindow) {
                        return;
                    }
                    
                    // Verify the origin matches
                    if (event.origin !== window.location.origin) {
                        console.warn('DEBUG: Received message from unexpected origin:', event.origin);
                        return;
                    }
                    
                    // Handle registration success
                    if (event.data.type === 'GAA_REGISTER_SUCCESS') {
                        cleanup();
                        
                        // Close the registration window
                        if (registerWindow && !registerWindow.closed) {
                            registerWindow.close();
                        }
                        
                        // Update the parent window's user authentication state
                        if (event.data.userState.granted && event.data.userState.id) {
                            window.userAuthenticated = true;
                            
                            // Trigger a custom event that the app can listen for to update UI
                            window.dispatchEvent(new CustomEvent('gaa-login-complete', {
                                detail: { userState: event.data.userState }
                            }));
                        }
                        
                        resolve(event.data.userState);
                    }
                    // Handle registration cancellation
                    else if (event.data.type === 'GAA_REGISTER_CANCELLED') {
                        cleanup();
                        
                        // Close the registration window
                        if (registerWindow && !registerWindow.closed) {
                            registerWindow.close();
                        }
                        
                        reject(new Error('Registration cancelled by user'));
                    }
                };
                
                // Add the message listener
                window.addEventListener('message', messageHandler);
                
                // Check if window was closed without completing registration
                checkWindowClosed = setInterval(() => {
                    if (registerWindow.closed) {
                        cleanup();
                        reject(new Error('Registration window was closed'));
                    }
                }, 500);
                
                // Cleanup on timeout (10 minutes - longer for registration)
                timeoutId = setTimeout(() => {
                    if (!registerWindow.closed) {
                        cleanup();
                        registerWindow.close();
                        reject(new Error('Registration timeout'));
                    }
                }, 600000);
            });
        },

        /**
         * Handle user login for existing users using cross-window messaging
         * 
         * @param {Number} postId The post ID
         * @returns {Promise} Promise that resolves with userState
         */
        async handleUserLogin(postId) {
            return new Promise((resolve, reject) => {
                // Build the login URL with parameters
                const loginUrl = `/gaa/login?post_id=${postId}&origin=${encodeURIComponent(window.location.origin)}`;
                
                // Open login window as a centered popup
                const width = 500;
                const height = 600;
                const left = (window.screen.width - width) / 2;
                const top = (window.screen.height - height) / 2;
                
                const loginWindow = window.open(
                    loginUrl,
                    'gaa-login',
                    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
                );
                
                if (!loginWindow) {
                    // Resolve with default state instead of rejecting
                    // This allows Google Extended Access to reset properly
                    resolve({
                        granted: false,
                        id: null
                    });
                    return;
                }
                
                let checkWindowClosed = null;
                let timeoutId = null;
                
                // Cleanup function
                const cleanup = () => {
                    if (checkWindowClosed) {
                        clearInterval(checkWindowClosed);
                        checkWindowClosed = null;
                    }
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                        timeoutId = null;
                    }
                    window.removeEventListener('message', messageHandler);
                };
                
                // Listen for messages from the login window
                const messageHandler = (event) => {
                    // Verify the message is from our login window
                    if (event.source !== loginWindow) {
                        return;
                    }
                    
                    // Verify the origin matches
                    if (event.origin !== window.location.origin) {
                        console.warn('DEBUG: Received message from unexpected origin:', event.origin);
                        return;
                    }
                    
                    // Handle login success
                    if (event.data.type === 'GAA_LOGIN_SUCCESS') {
                        cleanup();
                        
                        // Close the login window
                        if (loginWindow && !loginWindow.closed) {
                            loginWindow.close();
                        }
                        
                        // Update the parent window's user authentication state
                        window.userAuthenticated = true;
                        
                        // Always trigger the login complete event to reload the page
                        // This ensures the session is properly loaded and the UI updates
                        window.dispatchEvent(new CustomEvent('gaa-login-complete', {
                            detail: { userState: event.data.userState }
                        }));
                        
                        resolve(event.data.userState);
                    }
                    // Handle login cancellation
                    else if (event.data.type === 'GAA_LOGIN_CANCELLED') {
                        cleanup();
                        
                        // Close the login window
                        if (loginWindow && !loginWindow.closed) {
                            loginWindow.close();
                        }
                        
                        // Resolve with default state instead of rejecting
                        // This allows Google Extended Access to reset properly and be clickable again
                        resolve({
                            granted: false,
                            id: null
                        });
                    }
                };
                
                // Add the message listener
                window.addEventListener('message', messageHandler);
                
                // Check if window was closed without completing login
                checkWindowClosed = setInterval(() => {
                    if (loginWindow.closed) {
                        cleanup();
                        // Resolve with default state instead of rejecting
                        // This allows Google Extended Access to reset properly
                        resolve({
                            granted: false,
                            id: null
                        });
                    }
                }, 500);
                
                // Cleanup on timeout (5 minutes)
                timeoutId = setTimeout(() => {
                    if (!loginWindow.closed) {
                        cleanup();
                        loginWindow.close();
                        // Resolve with default state instead of rejecting
                        // This allows Google Extended Access to reset properly
                        resolve({
                            granted: false,
                            id: null
                        });
                    }
                }, 300000);
            });
        },

        /**
         * Unlock article when user gets Extended Access
         * This triggers the component to show the full article (like rfs=1)
         * 
         * @param {Object} entitlementResponse Google entitlement response
         */
        unlockArticle(entitlementResponse) {
            // Mark that Extended Access was granted (user dismissed CTA)
            this.gaaCtaShown = false;
            this.gaaCtaShownTimestamp = null;
            
            // Record article consumption for Extended Access metering
            // This tracks that the user read an article via Google Extended Access
            // so we can enforce the limit (typically 2-3 articles)
            // Use fire-and-forget approach to avoid blocking article unlock
            const postId = this.post?.id;
            if (postId) {
                // Use setTimeout to make this truly async and non-blocking
                setTimeout(() => {
                    window.axios.post('/api/gaa/record-consumption', {
                        postId: postId
                    }).then(response => {
                        if (response.data && response.data.data) {
                            /*
                            console.log('DEBUG: Consumption details:', {
                                consumed_id: response.data.data.consumed_id,
                                total_reads: response.data.data.total_extended_access_reads
                            });
                            */
                        }
                    }).catch(error => {
                        // Log error but don't block - consumption recording is best-effort
                        // If it fails, the user can still read the article
                        // The limit check will work on next page load when getUserState is called
                        console.error('DEBUG: Failed to record Extended Access consumption:', error);
                        console.error('DEBUG: Error response:', error.response?.data);
                        console.error('DEBUG: Error status:', error.response?.status);
                    });
                }, 100); // Small delay to ensure article unlock happens first
            } else {
                console.warn('DEBUG: Cannot record consumption - post ID not available');
            }
            
            // Emit event to bubble up to Single.vue to set showFull = true
            this.$emit('gaa-unlock-article', entitlementResponse);
        },

        /**
         * Show paywall when user doesn't have access
         * This triggers the component to show the preview/paywall view
         * 
         * According to Google docs, this is called in two scenarios:
         * 1. When Extended Access is not granted (show article paywall)
         * 2. When user clicks Subscribe on the Extended Access CTA (redirect to subscription)
         */
        showPaywall() {
            // Check if this is scenario 2: User clicked Subscribe on Extended Access CTA
            // According to Google docs, showPaywall is called when:
            // 1. Extended Access is not granted (show article paywall)
            // 2. User clicks Subscribe on the Extended Access CTA (redirect to subscription)
            //
            // To detect scenario 2, we check:
            // - CTA was shown recently (within last 10 seconds) AND
            // - User is registered (has id and registrationTimestamp) AND
            // - User doesn't have subscription AND
            // - User doesn't have access (granted: false)
            const isRegisteredUser = !!this.userState?.id && !!this.userState?.registrationTimestamp;
            const hasNoSubscription = !this.userState?.subscriptionTimestamp;
            const hasNoAccess = !this.userState?.granted;
            const ctaWasShownRecently = this.gaaCtaShown && 
                                      this.gaaCtaShownTimestamp && 
                                      (Date.now() - this.gaaCtaShownTimestamp) < 10000; // 10 seconds
            
            const isSubscribeClick = ctaWasShownRecently && 
                                   isRegisteredUser && 
                                   hasNoSubscription && 
                                   hasNoAccess;
            
            if (isSubscribeClick) {
                // Reset the flag
                this.gaaCtaShown = false;
                this.gaaCtaShownTimestamp = null;
                
                // Handle subscription redirect
                this.handleSubscribeRequest({ source: 'showPaywall-subscribe-click' });
                return; // Don't show paywall, redirect instead
            }
            
            // Scenario 1: Extended Access not granted - show article paywall
            // BUT: If user should see the CTA (registered, no subscription, no access),
            // we should NOT show paywall immediately - let Google show the CTA first
            const shouldSeeCTA = isRegisteredUser && hasNoSubscription && hasNoAccess;
            
            if (shouldSeeCTA && !ctaWasShownRecently) {
                // User should see CTA but it hasn't been shown yet (or was shown more than 10 seconds ago)
                // This might be Google calling showPaywall before showing the CTA, or Google is still processing
                // Don't show paywall yet - let Google show the CTA first
                // Give Google up to 30 seconds to show the CTA before we show the paywall
                const timeSinceInit = Date.now() - (this.gaaInitTimestamp || Date.now());
                const maxWaitTime = 30000; // 30 seconds
                
                if (timeSinceInit < maxWaitTime) {
                    // Don't reset CTA tracking - let it be set when CTA actually appears
                    // Don't emit show-paywall event - let Google handle showing the CTA
                    return;
                }
            }
            
            // Show paywall (either Extended Access was denied, or CTA was shown and dismissed)
            // Reset CTA tracking since we're showing paywall
            this.gaaCtaShown = false;
            this.gaaCtaShownTimestamp = null;
            
            // Emit event to bubble up to Single.vue to set showFull = false
            this.$emit('gaa-show-paywall');
        },

        /**
         * Handle subscription request from Google Extended Access CTA
         * This is called when user clicks "Subscribe" on the Extended Access CTA
         * 
         * @param {Object} subscribeRequest The subscription request from Google
         */
        handleSubscribeRequest(subscribeRequest) {
            // Get the publication from the post
            const post = this.post;
            if (!post || !post.publication) {
                console.error('DEBUG: Cannot handle subscription request - post or publication not available');
                console.error('DEBUG: Post:', post);
                console.error('DEBUG: Available in this:', Object.keys(this));
                
                // Try to get post from window or other sources as fallback
                // This might happen if the callback loses context
                return;
            }
            
            const publication = post.publication;
            const publicationSlug = publication.slug || publication.value || publication.ident;
            const publicationId = publication.id;
            
            // Determine subscription URL based on publication slug or ID
            let subscriptionUrl = null;
            
            // Try slug first
            if (publicationSlug === 'kingston-wire' || publicationSlug === 'kw') {
                subscriptionUrl = '/kwsubscribe';
            } else if (publicationSlug === 'shawangunk-journal' || publicationSlug === 'sj') {
                subscriptionUrl = '/sjsubscribe';
            } else if (publicationId) {
                // Fallback to ID-based detection
                // Shawangunk Journal is ID 1
                if (publicationId === 1) {
                    subscriptionUrl = '/sjsubscribe';
                } else {
                    // For other publications, try to determine from ID or use default
                    // You can add more publication IDs here as needed
                    subscriptionUrl = '/subscribe';
                }
            } else {
                // Default to general subscribe page
                subscriptionUrl = '/subscribe';
            }
            
            // Redirect to subscription page
            try {
                if (this.$router) {
                    this.$router.push(subscriptionUrl).catch(err => {
                        // Ignore navigation duplicate errors
                        if (err.name !== 'NavigationDuplicated') {
                            console.error('DEBUG: Router navigation error:', err);
                        }
                        // Fallback to window.location
                        window.location.href = subscriptionUrl;
                    });
                } else {
                    // Fallback to window.location if router not available
                    window.location.href = subscriptionUrl;
                }
            } catch (error) {
                console.error('DEBUG: Error redirecting to subscription page:', error);
                // Last resort - use window.location
                window.location.href = subscriptionUrl;
            }
        },

        /**
         * Handle Subscribe with Google entitlements
         * 
         * @param {Object} entitlementResponse SwG entitlement response
         */
        handleSwGEntitlement(entitlementResponse) {
            // Handle Subscribe with Google subscription
            // This would typically unlock the article or redirect to subscription management
            //this.$emit('swg-entitlement', entitlementResponse);
        },

        /**
         * Get allowed referrers for Google Extended Access
         * 
         * @returns {Array} Array of allowed referrer domains
         */
        getAllowedReferrers() {
            try {
                const protocol = window.location.protocol;
                const hostname = window.location.hostname;
                const port = window.location.port;
                const portSuffix = port ? `:${port}` : '';
                
                const referrers = [
                    `${hostname}${portSuffix}`,
                    'shawangunkjournal.com',
                    'www.shawangunkjournal.com',
                ];

                return referrers;
            } catch (error) {
                console.error('DEBUG: Error in getAllowedReferrers:', error);
                return [];
            }
        },
    }
};
